import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { getCurrentFiscalYear, getFiscalYearStartDate, getFiscalYearEndDate } from '../utils/fiscalYear'
import type { Transaction, AccountType, TransactionType } from '../types'

interface TransactionFilters {
  year?: number
  fiscalYear?: number  // 会計年度でのフィルタリング用
  accountType?: AccountType
  type?: TransactionType
  category?: string
  startDate?: string
  endDate?: string
}

// 現在の暦年を取得（将来的な拡張用にコメントアウト）
// const getCurrentYear = (): number => {
//   return new Date().getFullYear()
// }

// 組織設定を取得するフック
export const useOrganizationSettings = () => {
  const { organization } = useAuth()

  return useQuery({
    queryKey: ['organization-settings', organization?.id],
    queryFn: async () => {
      if (!organization) throw new Error('Organization not found')

      const { data, error } = await supabase
        .from('organizations')
        .select('fiscal_year_start_month')
        .eq('id', organization.id)
        .single()

      if (error) throw error
      
      return {
        fiscalYearStartMonth: data?.fiscal_year_start_month || 4
      }
    },
    enabled: !!organization?.id,
    staleTime: 1000 * 60 * 30, // 30分間はfreshとみなす（設定は頻繁に変更されない）
  })
}

export const useTransactions = (filters?: TransactionFilters) => {
  const { user, organization } = useAuth()
  const { data: settings } = useOrganizationSettings()

  return useQuery({
    queryKey: ['transactions', user?.id, organization?.id, filters, settings?.fiscalYearStartMonth],
    queryFn: async () => {
      if (!user || !organization) throw new Error('Not authenticated')

      let query = supabase
        .from('transactions')
        .select('*')
        .order('transaction_date', { ascending: false })

      // 会計年度フィルター
      if (filters?.fiscalYear && settings?.fiscalYearStartMonth) {
        const startDate = getFiscalYearStartDate(filters.fiscalYear, settings.fiscalYearStartMonth)
        const endDate = getFiscalYearEndDate(filters.fiscalYear, settings.fiscalYearStartMonth)
        query = query
          .gte('transaction_date', startDate.toISOString().split('T')[0])
          .lte('transaction_date', endDate.toISOString().split('T')[0])
      }
      // 暦年フィルター（後方互換性のため残す）
      else if (filters?.year) {
        const startDate = `${filters.year}-01-01`;
        const endDate = `${filters.year}-12-31`;
        query = query.gte('transaction_date', startDate).lte('transaction_date', endDate);
      }
      
      // その他のフィルター適用
      if (filters?.accountType) {
        query = query.eq('account_type', filters.accountType)
      }
      if (filters?.type) {
        query = query.eq('type', filters.type)
      }
      if (filters?.category) {
        query = query.eq('category', filters.category)
      }
      if (filters?.startDate) {
        query = query.gte('transaction_date', filters.startDate)
      }
      if (filters?.endDate) {
        query = query.lte('transaction_date', filters.endDate)
      }

      const { data, error } = await query

      if (error) throw error
      
      return data as Transaction[] || []
    },
    enabled: !!user && !!organization,
    staleTime: 1000 * 60 * 5, // 5分間はfreshとみなす
  })
}

export const useAccountBalances = () => {
  const { user, organization } = useAuth()

  return useQuery({
    queryKey: ['account-balances', user?.id, organization?.id],
    queryFn: async () => {
      if (!user || !organization) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('account_balances')
        .select('*')
        .eq('organization_id', organization.id)

      if (error) throw error
      return data
    },
    enabled: !!user && !!organization,
    staleTime: 1000 * 60 * 2, // 2分間はfreshとみなす
  })
}

export const useFinancialSummary = (year: number) => {
  const { user, organization } = useAuth()

  return useQuery({
    queryKey: ['financial-summary', user?.id, organization?.id, year],
    queryFn: async () => {
      if (!user || !organization) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .rpc('get_financial_summary', {
          org_id: organization.id,
          year: year
        })

      if (error) throw error
      return data
    },
    enabled: !!user && !!organization && !!year,
    staleTime: 1000 * 60 * 5, // 5分間はfreshとみなす
  })
}

export const useYearlyData = (years: number = 5) => {
  const { user, organization } = useAuth()

  return useQuery({
    queryKey: ['yearly-data', user?.id, organization?.id, years],
    queryFn: async () => {
      if (!user || !organization) throw new Error('Not authenticated')

      // データが存在する年の範囲を取得
      const { data: yearRange, error: yearRangeError } = await supabase
        .from('transactions')
        .select('transaction_date')
        .order('transaction_date', { ascending: true })
        .limit(1)
      
      const { data: yearRangeMax, error: yearRangeMaxError } = await supabase
        .from('transactions')
        .select('transaction_date')
        .order('transaction_date', { ascending: false })
        .limit(1)

      if (yearRangeError || yearRangeMaxError) throw yearRangeError || yearRangeMaxError

      let yearList = []
      if (yearRange && yearRange.length > 0 && yearRangeMax && yearRangeMax.length > 0) {
        const minYear = new Date(yearRange[0].transaction_date).getFullYear()
        const maxYear = new Date(yearRangeMax[0].transaction_date).getFullYear()
        
        for (let year = minYear; year <= maxYear; year++) {
          yearList.push(year)
        }
      } else {
        // データがない場合は現在年のみ
        yearList = [new Date().getFullYear()]
      }
      
      const yearlyData = []
      
      for (const calendarYear of yearList) {
        const startDate = `${calendarYear}-01-01`;
        const endDate = `${calendarYear}-12-31`;
        
        const { data, error } = await supabase
          .from('transactions')
          .select('account_type, type, amount')
          .gte('transaction_date', startDate)
          .lte('transaction_date', endDate)

        if (error) throw error

        // 年別集計
        const yearData = {
          year: `${calendarYear}年`,
          generalIncome: 0,
          generalExpense: 0,
          reserveIncome: 0,
          reserveExpense: 0
        }

        data?.forEach(transaction => {
          const key = `${transaction.account_type}${transaction.type === 'income' ? 'Income' : 'Expense'}` as 
            'generalIncome' | 'generalExpense' | 'reserveIncome' | 'reserveExpense'
          yearData[key] += transaction.amount
        })

        yearlyData.push(yearData)
      }

      return yearlyData
    },
    enabled: !!user && !!organization,
    staleTime: 1000 * 60 * 10, // 10分間はfreshとみなす（年次データは更新頻度低め）
  })
}

// 会計年度ベースの年別データを取得
export const useFiscalYearlyData = (years: number = 5, baseFiscalYear?: number) => {
  const { user, organization } = useAuth()
  const { data: settings, isLoading: settingsLoading } = useOrganizationSettings()

  return useQuery({
    queryKey: ['fiscal-yearly-data', user?.id, organization?.id, years, settings?.fiscalYearStartMonth, baseFiscalYear],
    queryFn: async () => {
      if (!user || !organization || !settings?.fiscalYearStartMonth) {
        throw new Error('Not authenticated or organization settings not available')
      }

      const fiscalYearStartMonth = settings.fiscalYearStartMonth  // 必ずDBの値を使用
      
      // データが存在する年度の範囲を取得
      const { data: dateRange, error: dateRangeError } = await supabase
        .from('transactions')
        .select('transaction_date')
        .order('transaction_date', { ascending: true })
        .limit(1)
      
      const { data: dateRangeMax, error: dateRangeMaxError } = await supabase
        .from('transactions')
        .select('transaction_date')
        .order('transaction_date', { ascending: false })
        .limit(1)

      if (dateRangeError || dateRangeMaxError) throw dateRangeError || dateRangeMaxError

      let yearList = []
      if (dateRange && dateRange.length > 0 && dateRangeMax && dateRangeMax.length > 0) {
        const minDate = new Date(dateRange[0].transaction_date)
        const maxDate = new Date(dateRangeMax[0].transaction_date)
        
        // 最小日付と最大日付から会計年度を算出
        const minFiscalYear = getFiscalYear(minDate, fiscalYearStartMonth)
        const maxFiscalYear = getFiscalYear(maxDate, fiscalYearStartMonth)
        
        for (let year = minFiscalYear; year <= maxFiscalYear; year++) {
          yearList.push(year)
        }
      } else {
        // データがない場合は現在年度のみ
        const currentFiscalYear = baseFiscalYear || getCurrentFiscalYear(fiscalYearStartMonth)
        yearList = [currentFiscalYear]
      }
      
      // 日付から会計年度を算出するヘルパー関数
      function getFiscalYear(date: Date, fiscalStartMonth: number): number {
        const year = date.getFullYear()
        const month = date.getMonth() + 1
        return month >= fiscalStartMonth ? year : year - 1
      }
      
      const yearlyData = []
      
      for (const fiscalYear of yearList) {
        const startDate = getFiscalYearStartDate(fiscalYear, fiscalYearStartMonth)
        const endDate = getFiscalYearEndDate(fiscalYear, fiscalYearStartMonth)
        
        const { data, error } = await supabase
          .from('transactions')
          .select('account_type, type, amount')
          .gte('transaction_date', startDate.toISOString().split('T')[0])
          .lte('transaction_date', endDate.toISOString().split('T')[0])

        if (error) throw error

        // 会計年度別集計
        const yearData = {
          year: `${fiscalYear}年度`,
          generalIncome: 0,
          generalExpense: 0,
          reserveIncome: 0,
          reserveExpense: 0
        }

        data?.forEach(transaction => {
          const key = `${transaction.account_type}${transaction.type === 'income' ? 'Income' : 'Expense'}` as 
            'generalIncome' | 'generalExpense' | 'reserveIncome' | 'reserveExpense'
          yearData[key] += transaction.amount
        })

        yearlyData.push(yearData)
      }

      return yearlyData
    },
    enabled: !!user && !!organization && !settingsLoading,  // 設定の読み込み完了を待つ
    staleTime: 1000 * 60 * 10, // 10分間はfreshとみなす（年次データは更新頻度低め）
  })
}

// 現在の会計年度を計算するヘルパー関数（現在未使用、将来の会計年度機能用に保持）
// const getCurrentFiscalYear = (fiscalYearStart: number): number => {
//   const now = new Date()
//   const month = now.getMonth() + 1
//   return month >= fiscalYearStart ? now.getFullYear() : now.getFullYear() - 1
// }

// データが存在する年度範囲を取得するフック
export const useAvailableFiscalYears = () => {
  const { user, organization } = useAuth()
  const { data: settings } = useOrganizationSettings()

  return useQuery({
    queryKey: ['available-fiscal-years', user?.id, organization?.id, settings?.fiscalYearStartMonth],
    queryFn: async () => {
      if (!user || !organization || !settings?.fiscalYearStartMonth) {
        throw new Error('Not authenticated or organization settings not available')
      }

      const fiscalYearStartMonth = settings.fiscalYearStartMonth

      // データが存在する年度の範囲を取得
      const { data: dateRange, error: dateRangeError } = await supabase
        .from('transactions')
        .select('transaction_date')
        .order('transaction_date', { ascending: true })
        .limit(1)
      
      const { data: dateRangeMax, error: dateRangeMaxError } = await supabase
        .from('transactions')
        .select('transaction_date')
        .order('transaction_date', { ascending: false })
        .limit(1)

      if (dateRangeError || dateRangeMaxError) throw dateRangeError || dateRangeMaxError

      let availableYears = []
      if (dateRange && dateRange.length > 0 && dateRangeMax && dateRangeMax.length > 0) {
        const minDate = new Date(dateRange[0].transaction_date)
        const maxDate = new Date(dateRangeMax[0].transaction_date)
        
        // 最小日付と最大日付から会計年度を算出
        const minFiscalYear = getFiscalYear(minDate, fiscalYearStartMonth)
        const maxFiscalYear = getFiscalYear(maxDate, fiscalYearStartMonth)
        
        for (let year = minFiscalYear; year <= maxFiscalYear; year++) {
          availableYears.push(year)
        }
      } else {
        // データがない場合は現在年度のみ
        const currentFiscalYear = getCurrentFiscalYear(fiscalYearStartMonth)
        availableYears = [currentFiscalYear]
      }
      
      function getFiscalYear(date: Date, fiscalStartMonth: number): number {
        const year = date.getFullYear()
        const month = date.getMonth() + 1
        return month >= fiscalStartMonth ? year : year - 1
      }

      return availableYears
    },
    enabled: !!user && !!organization && !!settings?.fiscalYearStartMonth,
    staleTime: 1000 * 60 * 30, // 30分間はfreshとみなす（データ範囲は頻繁に変更されない）
  })
}

export const useMonthlyData = (year: number) => {
  const { user, organization } = useAuth()

  return useQuery({
    queryKey: ['monthly-data', user?.id, organization?.id, year],
    queryFn: async () => {
      if (!user || !organization) throw new Error('Not authenticated')

      // 暦年の日付範囲を計算（将来的な拡張用にコメントアウト）
      // const startDate = `${year}-01-01`
      // const endDate = `${year}-12-31`

      // 暦年の日付範囲を計算
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;

      const { data, error } = await supabase
        .from('transactions')
        .select('transaction_date, account_type, type, amount')
        .gte('transaction_date', startDate)
        .lte('transaction_date', endDate)

      if (error) throw error

      // データを月別・会計種別に集計
      const monthlyData: Record<number, {
        generalIncome: number
        generalExpense: number
        reserveIncome: number
        reserveExpense: number
      }> = {}

      // 1-12月を初期化（fiscal_month）
      for (let month = 1; month <= 12; month++) {
        monthlyData[month] = {
          generalIncome: 0,
          generalExpense: 0,
          reserveIncome: 0,
          reserveExpense: 0
        }
      }

      // データを集計（月ベース）
      data?.forEach(transaction => {
        const transactionDate = new Date(transaction.transaction_date)
        const month = transactionDate.getMonth() + 1 // 0-indexed to 1-indexed
        const key = `${transaction.account_type}${transaction.type === 'income' ? 'Income' : 'Expense'}`
        monthlyData[month][key as keyof typeof monthlyData[1]] += transaction.amount
      })

      // 動的な月名生成（将来的な拡張用にコメントアウト）
      // const generateMonthNames = (startMonth: number): string[] => {
      //   const allMonths = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
      //   const result = []
      //   for (let i = 0; i < 12; i++) {
      //     const monthIndex = (startMonth - 1 + i) % 12
      //     result.push(allMonths[monthIndex])
      //   }
      //   return result
      // }

      const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']

      // 暦月順に配列を作成（1-12月順）
      return Array.from({length: 12}, (_, index) => ({
        month: `${year}年${monthNames[index]}`,
        ...monthlyData[index + 1]
      }))
    },
    enabled: !!user && !!organization && !!year,
    staleTime: 1000 * 60 * 5, // 5分間はfreshとみなす
  })
}

// 会計年度ベースの月別データを取得
export const useFiscalMonthlyData = (fiscalYear: number) => {
  const { user, organization } = useAuth()
  const { data: settings, isLoading: settingsLoading } = useOrganizationSettings()

  return useQuery({
    queryKey: ['fiscal-monthly-data', user?.id, organization?.id, fiscalYear, settings?.fiscalYearStartMonth],
    queryFn: async () => {
      if (!user || !organization || !settings?.fiscalYearStartMonth) {
        throw new Error('Not authenticated or organization settings not available')
      }

      const fiscalYearStartMonth = settings.fiscalYearStartMonth  // 必ずDBの値を使用
      const startDate = getFiscalYearStartDate(fiscalYear, fiscalYearStartMonth)
      const endDate = getFiscalYearEndDate(fiscalYear, fiscalYearStartMonth)

      const { data, error } = await supabase
        .from('transactions')
        .select('transaction_date, account_type, type, amount')
        .gte('transaction_date', startDate.toISOString().split('T')[0])
        .lte('transaction_date', endDate.toISOString().split('T')[0])

      if (error) throw error

      // データを月別・会計種別に集計（会計年度順）
      const monthlyData: Record<number, {
        generalIncome: number
        generalExpense: number
        reserveIncome: number
        reserveExpense: number
      }> = {}

      // 会計年度の12ヶ月を初期化
      for (let i = 0; i < 12; i++) {
        // const monthIndex = (fiscalYearStartMonth - 1 + i) % 12 + 1  // 未使用のため削除
        monthlyData[i + 1] = {
          generalIncome: 0,
          generalExpense: 0,
          reserveIncome: 0,
          reserveExpense: 0
        }
      }

      // データを集計
      data?.forEach(transaction => {
        const transactionDate = new Date(transaction.transaction_date)
        const fiscalMonthIndex = getFiscalMonthIndex(transactionDate, fiscalYearStartMonth)
        const key = `${transaction.account_type}${transaction.type === 'income' ? 'Income' : 'Expense'}`
        monthlyData[fiscalMonthIndex][key as keyof typeof monthlyData[1]] += transaction.amount
      })

      // 会計年度順の月名を生成
      const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
      const fiscalMonthNames: string[] = []
      
      for (let i = 0; i < 12; i++) {
        const monthIndex = (fiscalYearStartMonth - 1 + i) % 12
        const year = fiscalYear + (fiscalYearStartMonth + i > 12 ? 1 : 0)
        fiscalMonthNames.push(`${year}年${monthNames[monthIndex]}`)
      }

      // 会計年度順に配列を作成
      return Array.from({length: 12}, (_, index) => ({
        month: fiscalMonthNames[index],
        ...monthlyData[index + 1]
      }))
    },
    enabled: !!user && !!organization && !settingsLoading && !!fiscalYear,  // 設定の読み込み完了を待つ
    staleTime: 1000 * 60 * 5, // 5分間はfreshとみなす
  })
}

// 会計年度内の月のインデックスを取得するヘルパー関数
const getFiscalMonthIndex = (date: Date, fiscalYearStartMonth: number): number => {
  const month = date.getMonth() + 1
  if (month >= fiscalYearStartMonth) {
    return month - fiscalYearStartMonth + 1
  } else {
    return month + 12 - fiscalYearStartMonth + 1
  }
}